import {
  Component,
  ElementRef,
  input,
  signal,
  viewChild,
  effect,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core';

@Component({
  selector: 'ol-audio-player',
  templateUrl: './audio-player.component.html',
  styleUrls: ['./audio-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioPlayerComponent implements OnDestroy {
  audioUrl = input.required<string>();
  audioDuration = input<number>();

  audioElement = viewChild<ElementRef<HTMLAudioElement>>('audioElement');
  isPlaying = signal(false);
  currentTime = signal(0);
  duration = signal(0);

  waveformBars = Array.from({ length: 25 }, () => Math.random() * 60 + 20);

  private audioListeners: (() => void)[] = [];

  constructor() {
    effect(() => {
      const audio = this.audioElement()?.nativeElement;
      if (audio) {
        this.setupAudioListeners(audio);
      }
    });

    effect(() => {
      const providedDuration = this.audioDuration();
      if (providedDuration) {
        this.duration.set(providedDuration);
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanupListeners();
  }

  private setupAudioListeners(audio: HTMLAudioElement): void {
    this.cleanupListeners();

    const timeUpdateListener = () => {
      this.currentTime.set(audio.currentTime);
    };

    const loadedMetadataListener = () => {
      if (!this.audioDuration()) {
        this.duration.set(audio.duration);
      }
    };

    const endedListener = () => {
      this.isPlaying.set(false);
      this.currentTime.set(0);
    };

    audio.addEventListener('timeupdate', timeUpdateListener);
    audio.addEventListener('loadedmetadata', loadedMetadataListener);
    audio.addEventListener('ended', endedListener);

    this.audioListeners = [
      () => audio.removeEventListener('timeupdate', timeUpdateListener),
      () => audio.removeEventListener('loadedmetadata', loadedMetadataListener),
      () => audio.removeEventListener('ended', endedListener),
    ];
  }

  private cleanupListeners(): void {
    this.audioListeners.forEach(cleanup => cleanup());
    this.audioListeners = [];
  }

  togglePlayPause(): void {
    const audio = this.audioElement()?.nativeElement;
    if (!audio) return;

    if (this.isPlaying()) {
      audio.pause();
      this.isPlaying.set(false);
    } else {
      audio
        .play()
        .then(() => {
          this.isPlaying.set(true);
        })
        .catch(error => {
          console.error('Error playing audio:', error);
          this.isPlaying.set(false);
        });
    }
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  get displayDuration(): number {
    return this.audioDuration() || this.duration() || 0;
  }
}
